import { Badge, Button, Spinner } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useStores } from '../../../common/hooks';
import { BatchProgressStore } from '../../../models';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { FlexBox } from '../flex-box';
import { EvaluationModeBanner } from './EvaluationModeBanner';
import { ScrubCorrelationId } from './ScrubCorrelationId';

export type CancelInFlightKey = 'cancelAiScrubBatch' | 'cancelCountryAiScrubBatch';

interface BatchProgressIndicatorProps {
  store: BatchProgressStore;
  onCancel: () => void | Promise<void>;
  inFlightKey: CancelInFlightKey;
}

const BatchProgressIndicatorImpl: React.FC<BatchProgressIndicatorProps> = ({ store, onCancel, inFlightKey }) => {
  const { apiEndpointCallStatesStore } = useStores();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  if (!store.isBatchInProgress) return null;

  const isCancelling = apiEndpointCallStatesStore.inFlightStatusMap.get(inFlightKey) ?? false;
  const isQueued = store.batchProgress?.state === 'Queued';

  const confirmCancel = async (): Promise<void> => {
    await onCancel();
    setIsConfirmOpen(false);
  };

  return (
    <FlexBox direction='column'>
      <FlexBox aligns='center'>
        {isQueued
          ? <Badge appearance='tint' color='informative'>{store.batchProgressLabel}</Badge>
          : <Spinner size='extra-tiny' label={store.batchProgressLabel} labelPosition='after' />}
        {store.activeBatchRunId && <ScrubCorrelationId correlationId={store.activeBatchRunId} />}
        <Button
          appearance='secondary'
          disabled={isCancelling}
          onClick={() => setIsConfirmOpen(true)}
        >
          Cancel run
        </Button>
      </FlexBox>
      <EvaluationModeBanner show={!!store.isEvaluationRun} />
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        isLoading={isCancelling}
        dialogTitle='Cancel this batch run?'
        dialogSubtext='Queued nominations will stop. Nominations already scrubbed keep their results.'
        primaryButtonLabel='Cancel run'
        onPrimaryButtonClick={() => void confirmCancel()}
        secondaryButtonLabel='Keep running'
        onSecondaryButtonClick={() => setIsConfirmOpen(false)}
        onCloseDialogClick={() => setIsConfirmOpen(false)}
      />
    </FlexBox>
  );
};

export const BatchProgressIndicator = observer(BatchProgressIndicatorImpl);
